/**
 * Tick-level observation for a `MockEnvironment`.
 *
 * On a loaded page one `await` can return seconds later while dozens of bounded ticks ran inside the
 * gap; anything short-lived in between is spawned and gone unseen. A sampler that runs INSIDE the
 * tick cannot miss it. `onTick` is the framework's own `nextTickBeforeUpdate`/`nextTickAfterUpdate`
 * seam, self-re-arming (the seam is one-shot) and disposable.
 */
import type { ProcessResolver, ProcessTarget, PumpableProcess } from './environment-lookup.js';

/** Which half of a tick a listener runs in: `before` the systems update, or `after` they did. */
export type TickPhase = 'before' | 'after';

/** A tick listener: the tick's delta in MILLISECONDS, and the phase it runs in. */
export type TickListener = (deltaMs: number, phase: TickPhase) => void;

/** The tick-level queries every `MockEnvironment` answers, mixed into it by `initializer`. */
export interface TickQueries {
  /**
   * Run `listener` inside every tick of the target process(es): `before` carries the tick's delta and
   * the state the previous tick left, `after` what this tick produced — it runs after the work the systems'
   * `onUpdate` queued for this tick with `nextTickAfterUpdate`, so it sees what that work resolved. Re-arms
   * itself; the returned function stops it. A throw in the listener is logged by the framework and does not
   * end the subscription, and neither does a tick a system's throw aborts (see `subscribeProcessTick`).
   * The target resolves like `pump`'s (`'all'` subscribes each process once); one that names nothing throws here.
   */
  onTick(target: ProcessTarget | undefined, listener: TickListener): () => void;
  /**
   * Watch a mechanic play out through the ticks that produce it, and answer with what the window got
   * to see. Bounded on what was OBSERVED — sampled ticks and game time — never on wall time, except the
   * hang guard for a page that stops ticking. Never asserts: the caller's assertions own the verdict,
   * and `describeObservation(result)` belongs in their failure message. Prints one close line through
   * `env.report`.
   */
  observe(spec: ObserveSpec): Promise<ObserveResult>;
}

/** What `env.observe` watches, how it samples, and when it closes. */
export interface ObserveSpec {
  /** The processes whose EVERY tick is a sampling opportunity — and the window's ruler. Duplicates collapse. */
  on: Array<ProcessTarget | undefined>;
  /** One reading. Runs inside every tick of every `on` process, on every `emitters` emission, and once per wake. Read-only. */
  sample(): void;
  /**
   * Evaluated after every sample. Once it holds, the window closes at its next check — after the current
   * wake's `onWake` and `pollMs` wait — so ticks that run before then (a batch `onWake` pumps, or the page's
   * own loop) are still sampled and counted in `ticks`.
   */
  done(): boolean;
  /** Give-up horizon in GAME ms: on `clock` when given, else on the observed processes' own ticks — on the wall clock when `on` names no process. */
  gameBudgetMs: number;
  /** The budget never closes the window before this many ticks were SAMPLED (default 30; no floor when `on` names no process). `done` closes it at any time. */
  minTicks?: number;
  /** …and closes it at the first wake once this many ticks were sampled, however little game time passed (default: `gameBudgetMs` as 60 Hz frames, never below `minTicks`). */
  maxTicks?: number;
  /** The clock the budget is measured on; default: the observed ticks' own deltas. */
  clock?(): number;
  /** Extra in-tick seams: an emitter whose event IS the moment a transient exists. Subscribed for the window; taken off (and inert) once it closes. */
  emitters?: Array<{ target: { on(event: string, cb: () => void): unknown; off?(event: string, cb: () => void): unknown }; event: string }>;
  /** Once-per-wake side effects (pumping a starved authority). Never in `sample`. */
  onWake?(): void;
  /** Wall gap between wakes (default 10). Paces the side effects; not a bound. */
  pollMs?: number;
  /** Last-resort wall guard for a page that stops ticking: the window closes as `hang` once this long passed with no observed tick (default 90 000). A window that still ticks is never cut by it. */
  hangGuardMs?: number;
  /** Injectable wall clock (default `Date.now`) — the hang guard, `wallMs` and a process-less window's game time read it. It exists so a test can control wall time. */
  now?(): number;
  /** Names the window in the close line. */
  label?: string;
}

/** What an observation window got to see. */
export interface ObserveResult {
  /** Ticks SAMPLED on the busiest observed process. */
  ticks: number;
  /** Poll wakes. `1` is the signature of a starved page: one wake covered the whole window. */
  wakes: number;
  /** Game time the window covered: on `clock` when given, else the busiest observed process's ticks, else the wall clock. */
  gameMs: number;
  /** Wall cost; reported, never a bound. */
  wallMs: number;
  /** `done` = the caller's condition held; `budget` = the game budget (or `maxTicks`) ran out; `hang` = nothing ticked for `hangGuardMs`. */
  reason: 'done' | 'budget' | 'hang';
}

/** One-line render of an `ObserveResult` for a failing assertion's message. */
export const describeObservation = (r: ObserveResult): string =>
  `observed{ticks=${r.ticks} wakes=${r.wakes} gameMs=${Math.round(r.gameMs)} wallMs=${Math.round(r.wallMs)} end=${r.reason}}`;

/**
 * Subscribe one process; returns the stop. Each phase re-arms itself FIRST, so a throwing listener cannot end
 * the subscription. The `after` re-arms itself late — during its own after phase — so in the next tick it runs
 * AFTER the after-callbacks game code scheduled during this tick (`System.nextTickAfterUpdate` work): the
 * listener sees what that work resolved.
 *
 * A system whose `onUpdate` throws aborts its tick between the two phases (the game loops catch and log it), and
 * that tick's `after` is dropped with it — a self-re-arming `after` would be lost for good. So `before` also
 * arms a fallback for the next tick, stamped with that tick: it calls the listener only when the late `after`
 * is NOT queued for that tick (the tick before it never finished), and re-arms the late `after` from there.
 * Only the aborted tick's own after phase is missed; in the tick right after it, the listener runs before the
 * game's after-callbacks, once.
 */
export const subscribeProcessTick = (process: PumpableProcess, listener: TickListener): (() => void) => {
  let live = true;
  /** How many ticks this subscription has seen begin: the number of the tick running now. */
  let tick = 0;
  /** The tick the late `after` is queued for (armed at subscribe for the first tick). */
  let lateArmedFor = 1;
  const late = (delta: number) => {
    if (!live) return;
    lateArmedFor = tick + 1;
    process.nextTickAfterUpdate(late);
    listener(delta * 1000, 'after');
  };
  /** The next tick's stand-in: acts only when that tick's late `after` was lost with an aborted tick. */
  const fallback = (forTick: number) => (delta: number) => {
    if (!live || lateArmedFor === forTick) return;
    lateArmedFor = forTick + 1;
    process.nextTickAfterUpdate(late);
    listener(delta * 1000, 'after');
  };
  const before = (delta: number) => {
    if (!live) return;
    tick += 1;
    process.nextTickBeforeUpdate(before);
    process.nextTickAfterUpdate(fallback(tick + 1));
    listener(delta * 1000, 'before');
  };
  process.nextTickBeforeUpdate(before);
  process.nextTickAfterUpdate(late);
  return () => { live = false; };
};

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Builds `observe` over a resolver. `report` is read when a window closes, so a reassigned `env.report` is honoured. */
export const createObserve = (resolver: ProcessResolver, report: () => (...args: unknown[]) => void) =>
  async (spec: ObserveSpec): Promise<ObserveResult> => {
    const { sample, done, gameBudgetMs, minTicks = 30, clock, emitters = [], onWake, pollMs = 10, hangGuardMs = 90_000, now = Date.now, label = 'window' } = spec;
    if (!Number.isFinite(gameBudgetMs) || gameBudgetMs < 0) {
      throw new Error(`[test-utils] observe: gameBudgetMs must be a finite number of milliseconds ≥ 0 (got ${gameBudgetMs})`);
    }
    const maxTicks = spec.maxTicks ?? Math.max(minTicks, Math.ceil(gameBudgetMs / (1000 / 60)));
    const processes = [...new Set(spec.on.flatMap((target) => resolver.resolveTargets(target)))];
    // Per process, compared by MAX: two processes ticking side by side must not spend the budget twice as fast.
    const meters = processes.map(() => ({ ticks: 0, gameMs: 0 }));
    const ticksSeen = () => meters.reduce((most, m) => Math.max(most, m.ticks), 0);
    const tickGameMs = () => meters.reduce((most, m) => Math.max(most, m.gameMs), 0);
    const startedAt = now();
    // The hang guard's ruler: a window is hung only when NOTHING has ticked for `hangGuardMs`.
    let lastTickAt = startedAt;

    let finished = false;
    let closed = false;
    const take = () => {
      sample();
      if (!finished && done()) finished = true;
    };
    const stops = processes.map((process, i) => subscribeProcessTick(process, (deltaMs, phase) => {
      if (phase === 'before') {
        meters[i].ticks += 1;
        meters[i].gameMs += deltaMs;
        lastTickAt = now();
      }
      take();
    }));
    const offs = emitters.map(({ target, event }) => {
      // An emitter with no `off` keeps the handler after the window: `closed` makes it inert.
      const onEmit = () => { if (!closed) take(); };
      target.on(event, onEmit);
      return () => target.off?.(event, onEmit);
    });

    const clockStart = clock ? clock() : 0;
    const gameMs = (): number => (clock ? clock() - clockStart : processes.length ? tickGameMs() : now() - startedAt);
    // With no process to observe there is no tick to count: `minTicks` could never be met.
    const tickFloor = processes.length ? minTicks : 0;
    let wakes = 0;
    let reason: ObserveResult['reason'];
    try {
      take();
      for (;;) {
        if (finished) { reason = 'done'; break; }
        if (ticksSeen() >= tickFloor && (gameMs() >= gameBudgetMs || ticksSeen() >= maxTicks)) { reason = 'budget'; break; }
        if (now() - lastTickAt >= hangGuardMs) { reason = 'hang'; break; }
        wakes += 1;
        onWake?.();
        await sleep(pollMs);
        take();
      }
    } finally {
      closed = true;
      stops.forEach((stop) => stop());
      offs.forEach((off) => off());
    }
    const result: ObserveResult = { ticks: ticksSeen(), wakes, gameMs: gameMs(), wallMs: now() - startedAt, reason };
    report()(`[observe] ${label} — ${describeObservation(result)}`);
    return result;
  };

/**
 * Builds the tick queries for one environment over its resolver. `initializer` attaches the result to the env;
 * `report` returns where `observe` prints its close line (`() => env.report`, read at close).
 */
export const createTickQueries = (resolver: ProcessResolver, report: () => (...args: unknown[]) => void): TickQueries => ({
  onTick(target, listener) {
    const stops = [...new Set(resolver.resolveTargets(target))].map((process) => subscribeProcessTick(process, listener));
    return () => stops.forEach((stop) => stop());
  },
  observe: createObserve(resolver, report),
});
